import React, {useEffect, useState} from 'react';
import type {TableRowSelection} from 'antd/es/table/interface';
import {DDEXPageContext} from './DDEXMainPage';
import {ProFormText, ProFormUploadDragger,} from '@ant-design/pro-form';
import ProCard from '@ant-design/pro-card';
import {
    Button,
    Collapse,
    Image,
    message,
    Modal,
    Popconfirm,
    Progress,
    Row,
    Space,
    Spin,
    Table,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import axiosApiInstance from '../../../api/axiosClient';
import axios from 'axios';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    CustomerServiceOutlined,
    DeleteOutlined,
    FileImageOutlined,
    InfoCircleOutlined,
    SafetyOutlined,
    UploadOutlined, WarningOutlined
} from '@ant-design/icons';
import {atom, useAtom} from 'jotai';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import {useLocation} from 'react-router-dom';
import {
    fetchPresignedFileUrl,
    fetchReleaseFiles,
    mapWithConcurrency,
    UploadedFile,
    stripConsecutiveSpacesInFilename,
} from './fileUtils';

/** Parallel uploads to S3; keep low so browser + Contabo are not saturated. */
const UPLOAD_CONCURRENCY = 3;

dayjs.extend(duration);

// Atom to store uploaded files data
export const uploadedFilesAtom = atom<UploadedFile[]>([]);

// Using interfaces from fileUtils.ts

interface FileUploadStepProps {
    edit: boolean;
    isLoadingExistingFiles: boolean;
}

const formatDuration = (durationNs: number) => {
    // Convert nanoseconds to seconds for calculation
    const totalSeconds = durationNs / 1_000_000_000;

    // Extract whole minutes
    const minutes = Math.floor(totalSeconds / 60);

    // Extract remaining whole seconds
    const seconds = Math.floor(totalSeconds % 60);

    // Extract milliseconds
    const milliseconds = Math.round((totalSeconds % 1) * 1000);

    // Format the result as mm:ss:SSS
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');
    const formattedMilliseconds = String(milliseconds).padStart(3, '0');

    return `${formattedMinutes}:${formattedSeconds}.${formattedMilliseconds}`;
};

const getBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });

// Function to determine file type
const getFileType = (file: File): 'image' | 'audio' | 'other' => {
    if (file.type.startsWith('image/')) {
        return 'image';
    } else if (file.type.startsWith('audio/')) {
        return 'audio';
    } else {
        return 'other';
    }
};

// Interface for audio metadata
interface AudioMetadata {
    duration: number;
    channels?: number;
    sampleRate?: number;
    bitRate?: number;
}

// Interface for image metadata
interface ImageMetadata {
    width?: number;
    height?: number;
}

// Duration only — full decode was decoding entire WAVs in-browser (very slow). Server ffmpeg fills channels/sample rate.
const getAudioMetadata = async (file: File): Promise<AudioMetadata> => {
    return new Promise((resolve) => {
        const objectUrl = URL.createObjectURL(file);
        const audio = new Audio(objectUrl);
        const cleanup = () => URL.revokeObjectURL(objectUrl);

        audio.addEventListener('loadedmetadata', () => {
            const durationNs = Math.floor(audio.duration * 1_000_000_000);
            cleanup();
            resolve({ duration: durationNs });
        });

        audio.addEventListener('error', () => {
            cleanup();
            console.error('Error loading audio for metadata extraction');
            resolve({ duration: 0 });
        });
    });
};

// Function to get image metadata
const getImageMetadata = async (file: File): Promise<ImageMetadata> => {
    return new Promise((resolve) => {
        const img = new window.Image();
        img.onload = () => {
            resolve({
                width: img.width,
                height: img.height
            });
            // Clean up by revoking the object URL
            URL.revokeObjectURL(img.src);
        };
        img.onerror = () => {
            console.error('Error loading image for metadata extraction');
            resolve({});
            URL.revokeObjectURL(img.src);
        };
        img.src = URL.createObjectURL(file);
    });
};

const FileUploadStep = ({edit, isLoadingExistingFiles}: FileUploadStepProps) => {
    const [uploadedFiles, setUploadedFiles] = useAtom(uploadedFilesAtom);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [validationModalVisible, setValidationModalVisible] = useState(false);
    const [selectedFile, setSelectedFile] = useState<UploadedFile | null>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const location = useLocation();
    // Get the last segment of the URL path as the folder name
    const folderName = location.pathname.split('/').filter(Boolean).pop();

    const handlePreview = async (file: UploadedFile) => {
        if (file.fileType !== 'image') return;

        let preview = file.url;

        if (!preview && file.originFileObj) {
            preview = await getBase64(file.originFileObj);
        } else if (!preview && folderName) {
            preview = await fetchPresignedFileUrl(folderName, file.name);
        }

        setPreviewImage(preview || '');
        setPreviewTitle(file.name);
        setPreviewVisible(true);
    };


    const handleUploadMultiple = async (fileList: FileList | File[]) => {
        setUploading(true);
        setUploadProgress(0);
        const totalFiles = Array.from(fileList).length;
        let completedFiles = 0;

        // Process filenames to strip consecutive spaces
        const processedFiles = Array.from(fileList).map(file => stripConsecutiveSpacesInFilename(file));

        const uploadOne = async (file: File) => {
            try {
                // Create a unique ID for the file
                const uid = `file-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

                // Determine file type
                const fileType = getFileType(file);
                console.log({file},);
                console.log(`Uploading file type: ${fileType}`);

                // Create new file object
                const newFile: UploadedFile = {
                    uid,
                    name: file.name,
                    type: file.type,
                    fileType,
                    size: file.size,
                    originFileObj: file
                };

                // Extract metadata based on file type
                let audioMetadata: AudioMetadata | null = null;
                let imageMetadata: ImageMetadata | null = null;

                if (fileType === 'audio') {
                    audioMetadata = await getAudioMetadata(file);
                    newFile.duration = audioMetadata.duration;
                } else if (fileType === 'image') {
                    imageMetadata = await getImageMetadata(file);
                }

                // Upload file to server
                const response = await axiosApiInstance.post(
                    '/api/products/releases/generate-presigned-url-upload',
                    {
                        fileName: file.name,
                        fileType: file.type,
                        folderName: folderName,
                    }
                );

                const {presignedUrl} = response.data;

                await axios.put(presignedUrl, file, {
                    headers: {
                        'Content-Type': file.type
                    },
                    onUploadProgress: (progressEvent) => {
                        const fileProgress = (progressEvent.loaded * 100) / progressEvent.total;
                        setUploadProgress(prevProgress => {
                            const fileContribution = fileProgress / totalFiles;
                            const baseProgress = (completedFiles * 100) / totalFiles;
                            return Math.min(baseProgress + fileContribution, 100);
                        });
                    }
                });

                // After successful upload, call the update-file-metadata endpoint
                try {
                    const metadataPayload: any = {
                        fileName: file.name,
                        folderName: folderName
                    };

                    // Add audio metadata if available
                    if (audioMetadata) {
                        metadataPayload.duration = audioMetadata.duration;
                        metadataPayload.channels = audioMetadata.channels;
                        metadataPayload.sampleRate = audioMetadata.sampleRate;
                        metadataPayload.bitRate = audioMetadata.bitRate;
                    }

                    // Add image metadata if available
                    if (imageMetadata) {
                        metadataPayload.width = imageMetadata.width;
                        metadataPayload.height = imageMetadata.height;
                    }

                    await axiosApiInstance.post('/api/products/releases/update-file-metadata', metadataPayload);
                } catch (error) {
                    console.error('Error updating file metadata:', error);
                    // Continue even if metadata update fails
                }

                completedFiles++;
                return newFile;
            } catch (error) {
                console.error('Error uploading file:', error);
                message.error(`${file.name} upload failed`);
                completedFiles++;
                return null;
            }
        };

        try {
            const results = await mapWithConcurrency(processedFiles, UPLOAD_CONCURRENCY, uploadOne);
            const successfulUploads = results.filter(Boolean) as UploadedFile[];
            setUploadedFiles(prev => [...prev, ...successfulUploads]);
            message.success(`${successfulUploads.length} files uploaded successfully`);

            // Fast sync from S3; use Refresh Files for full validation
            void fetchReleaseFiles(folderName, setUploadedFiles, setRefreshing);
        } catch (error) {
            console.error('Error in batch upload:', error);
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const clearModalsForDeletedFile = (file: UploadedFile) => {
        if (previewVisible && previewTitle === file.name) {
            setPreviewVisible(false);
        }
        if (selectedFile && selectedFile.uid === file.uid) {
            setValidationModalVisible(false);
            setSelectedFile(null);
        }
    };

    const deleteFileOnServer = async (file: UploadedFile) => {
        await axiosApiInstance.post('/api/products/releases/delete-object', {
            fileName: file.name,
            folderName: folderName,
        });
    };

    const removeFilesFromState = (uids: Set<string>) => {
        setUploadedFiles((prev) => prev.filter((f) => !uids.has(f.uid)));
        setSelectedRowKeys((prev) => prev.filter((key) => !uids.has(String(key))));
    };

    const handleDeleteFile = async (file: UploadedFile) => {
        try {
            await deleteFileOnServer(file);
            clearModalsForDeletedFile(file);
            removeFilesFromState(new Set([file.uid]));
            message.success(`${file.name} deleted successfully`);
        } catch (error) {
            console.error('Error deleting file:', error);
            message.error(`Failed to delete ${file.name}`);
        }
    };

    const handleDeleteSelected = async () => {
        const selectedFiles = uploadedFiles.filter((f) => selectedRowKeys.includes(f.uid));
        if (selectedFiles.length === 0) {
            return;
        }

        const deletable = selectedFiles.filter((f) => f.processingStatus !== 'processing');
        if (deletable.length === 0) {
            message.warning('Selected files are still processing and cannot be deleted');
            return;
        }

        setBulkDeleting(true);
        const succeededUids = new Set<string>();
        const failedNames: string[] = [];

        await Promise.all(
            deletable.map(async (file) => {
                try {
                    await deleteFileOnServer(file);
                    clearModalsForDeletedFile(file);
                    succeededUids.add(file.uid);
                } catch (error) {
                    console.error('Error deleting file:', error);
                    failedNames.push(file.name);
                }
            }),
        );

        if (succeededUids.size > 0) {
            removeFilesFromState(succeededUids);
        }

        if (failedNames.length === 0) {
            message.success(
                succeededUids.size === 1
                    ? '1 file deleted successfully'
                    : `${succeededUids.size} files deleted successfully`,
            );
        } else if (succeededUids.size > 0) {
            message.warning(
                `Deleted ${succeededUids.size} file(s); failed: ${failedNames.join(', ')}`,
            );
        } else {
            message.error('Failed to delete selected files');
        }

        setBulkDeleting(false);
    };

    useEffect(() => {
        const validUids = new Set(uploadedFiles.map((f) => f.uid));
        setSelectedRowKeys((prev) => prev.filter((key) => validUids.has(String(key))));
    }, [uploadedFiles]);


    // Function to show validation details for a file
    const showValidationDetails = (file: UploadedFile) => {
        setSelectedFile(file);
        setValidationModalVisible(true);
    };

    // Helper function to format metadata for display in tooltip
    const formatMetadata = (metadata?: { [key: string]: string }) => {
        if (!metadata || Object.keys(metadata).length === 0) {
            return <div>No metadata available</div>;
        }

        return (
            <div style={{ maxWidth: '300px', maxHeight: '400px', overflow: 'auto' }}>
                <Typography.Title level={5}>File Metadata</Typography.Title>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                        {Object.entries(metadata).map(([key, value]) => (
                            <tr key={key} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <td style={{ padding: '4px 8px', fontWeight: 'bold', verticalAlign: 'top' }}>
                                    {key.replace(/_/g, ' ')}
                                </td>
                                <td style={{ padding: '4px 8px', wordBreak: 'break-word' }}>
                                    {key === 'duration' ? formatDuration(parseInt(value)) : value}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const columns = [
        {
            title: 'File Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: UploadedFile) => (
                <Space>
                    <Typography.Text
                        style={{cursor: record.fileType === 'image' ? 'pointer' : 'default'}}
                        onClick={() => record.fileType === 'image' && handlePreview(record)}
                    >
                        {text}
                    </Typography.Text>
                    {record.metadata && Object.keys(record.metadata).length > 0 && (
                        <Tooltip 
                            title={formatMetadata(record.metadata)} 
                            placement="right"
                            overlayInnerStyle={{ backgroundColor: 'var(--dm-elevated)', color: 'var(--dm-text)' }}
                        >
                            <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
                        </Tooltip>
                    )}
                </Space>
            )
        },
        {
            title: 'Type',
            dataIndex: 'fileType',
            key: 'fileType',
            render: (type: string) => {
                let color = 'default';
                if (type === 'image') color = 'blue';
                if (type === 'audio') color = 'green';
                return <Tag color={color}>{type.toUpperCase()}</Tag>;
            }
        },
        {
            title: 'Size',
            dataIndex: 'size',
            key: 'size',
            render: (size: number) => {
                const kb = size / 1024;
                if (kb < 1024) {
                    return `${kb.toFixed(2)} KB`;
                } else {
                    return `${(kb / 1024).toFixed(2)} MB`;
                }
            }
        },
        {
            title: 'Duration',
            dataIndex: 'duration',
            key: 'duration',
            render: (duration: number, record: UploadedFile) => {
                if (record.fileType !== 'audio' || !duration) return '-';
                return formatDuration(duration);
            }
        },
        {
            title: 'Processing',
            dataIndex: 'processingStatus',
            key: 'processingStatus',
            render: (processingStatus: string) => {
                let color = 'default';
                let text = processingStatus || 'Pending';

                if (processingStatus === 'success') {
                    color = 'success';
                    text = 'Success';
                } else if (processingStatus === 'processing') {
                    color = 'processing';
                    text = 'Processing';
                } else if (processingStatus === 'error') {
                    color = 'error';
                    text = 'Error';
                }

                return <Tag color={color}>{text}</Tag>;
            }
        },
        {
            title: 'Validation',
            key: 'validation',
            render: (_: any, record: UploadedFile) => {
                if (!record.validationResult) {
                    return <Tag color="default">Not Validated</Tag>;
                }

                if (record.validationResult.isValid) {
                    // For valid files, show a tooltip with platform-specific validation info
                    const tooltipContent = (
                        <div>
                            <div><strong>All validations passed</strong></div>
                            {record?.validationResult?.platformResults && (
                                <ul style={{margin: '5px 0 0 0', padding: '0 0 0 20px'}}>
                                    {Object.entries(record?.validationResult?.platformResults)?.map(([platform, result]) => (
                                        <li key={platform}>
                                            <span
                                                style={{textTransform: 'capitalize'}}>{platform}</span>: {result.isValid ? 'Valid' : 'Invalid'}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    );

                    return (
                        <Tooltip 
                            title={tooltipContent}
                            overlayInnerStyle={{ backgroundColor: 'var(--dm-elevated)', color: 'var(--dm-text)' }}
                        >
                            <Tag
                                color="success"
                                icon={<CheckCircleOutlined/>}
                                style={{cursor: 'pointer'}}
                                onClick={() => showValidationDetails(record)}
                            >
                                Valid
                            </Tag>
                        </Tooltip>
                    );
                } else {
                    // For invalid files, show a tooltip with issues summary
                    // const issueCount = record.validationResult?.issues?.length;
                    const platformIssues = record.validationResult.platformResults ?
                        Object.entries(record.validationResult.platformResults)
                            .filter(([_, result]) => !result.isValid)
                            .map(([platform, _]) => platform) : [];
                    const issueCount = platformIssues?.length;

                    const tooltipContent = (
                        <div>
                            <div><strong>{issueCount} issue{issueCount !== 1 ? 's' : ''} found</strong></div>
                            {platformIssues.length > 0 && (
                                <div>
                                    Invalid for: {platformIssues?.map(p =>
                                    <Tag key={p} /*size="small"*/ style={{
                                        textTransform: 'capitalize',
                                        margin: '0 2px',
                                        color: 'black'
                                    }}>{p}</Tag>
                                )}
                                </div>
                            )}
                            <div style={{marginTop: 5}}>Click for details</div>
                        </div>
                    );

                    return (
                        <Tooltip 
                            title={tooltipContent}
                            overlayInnerStyle={{ backgroundColor: 'var(--dm-elevated)', color: 'var(--dm-text)' }}
                        >
                            <Tag
                                color="warning"
                                icon={<WarningOutlined/>}
                                style={{cursor: 'pointer'}}
                                onClick={() => showValidationDetails(record)}
                            >
                                Invalid
                            </Tag>
                        </Tooltip>
                    );
                }
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: UploadedFile) => (
                <Space>
                    <Popconfirm
                        title="Delete file"
                        onConfirm={() => handleDeleteFile(record)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined/>}
                            disabled={record.processingStatus === 'processing'}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const rowSelection: TableRowSelection<UploadedFile> = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
        getCheckboxProps: (record) => ({
            disabled: record.processingStatus === 'processing' || bulkDeleting || uploading || refreshing,
        }),
    };

    const selectedCount = selectedRowKeys.length;

    return (
        <div>
            {/* Hidden field to store uploaded files data */}
            <ProFormText name="uploaded_files" hidden/>

            {/* Loading progress bar - positioned under the heading for better visibility */}
            {uploading && (
                <Row style={{marginBottom: 16}}>
                    <Typography.Text>Uploading files...</Typography.Text>
                    <Progress percent={Math.round(uploadProgress)} status="active" style={{width: '100%'}}/>
                </Row>
            )}

            {isLoadingExistingFiles ? (
                <div style={{textAlign: 'center', padding: '50px 0'}}>
                    <Spin size="large" tip="Loading existing files..."/>
                </div>
            ) : uploadedFiles.length === 0 ? (
                <ProFormUploadDragger
                    name="all_files"
                    fieldProps={{
                        multiple: true,
                        showUploadList: false,
                        beforeUpload: (file, fileList) => {
                            // For the first file in the batch or individual file
                            if (fileList.indexOf(file) === 0) {
                                handleUploadMultiple(fileList);
                            }
                            return false; // Prevent default upload behavior
                        }
                    }}
                    title={null}
                    description={
                        <Space direction="vertical" size="small" style={{textAlign: 'center', width: '100%'}}>
                            <Typography.Text strong>Click or drag files to this area to upload your
                                assets</Typography.Text>
                            <Typography.Text type="secondary">
                                Supports batch uploads for images (JPG, PNG, etc.) and audio (MP3, WAV, etc.).
                            </Typography.Text>
                            <Space>
                                <Tag icon={<FileImageOutlined/>} color="blue">Images</Tag>
                                <Tag icon={<CustomerServiceOutlined/>} color="green">Audio</Tag>
                            </Space>
                        </Space>
                    }
                    style={{
                        padding: '40px 20px',
                        background: '#fafafa',
                        border: '2px dashed #d9d9d9',
                        borderRadius: '8px'
                    }}
                />
            ) : null}

            {uploadedFiles.length > 0 && !isLoadingExistingFiles && (
                <ProCard
                    title="Uploaded Files"
                    style={{marginLeft: 16}}
                    bordered
                    headerBordered
                    extra={
                        <Space wrap>
                            {selectedCount > 0 && (
                                <Popconfirm
                                    title={`Delete ${selectedCount} selected file${selectedCount === 1 ? '' : 's'}?`}
                                    onConfirm={handleDeleteSelected}
                                    okText="Delete"
                                    cancelText="Cancel"
                                    okButtonProps={{danger: true}}
                                >
                                    <Button
                                        danger
                                        icon={<DeleteOutlined/>}
                                        loading={bulkDeleting}
                                        disabled={uploading || refreshing}
                                    >
                                        Delete selected ({selectedCount})
                                    </Button>
                                </Popconfirm>
                            )}
                            <Tooltip 
                                title="Refresh files list (also revalidates all files)"
                                overlayInnerStyle={{ backgroundColor: 'var(--dm-elevated)', color: 'var(--dm-text)' }}
                            >
                                <Button
                                    type="primary"
                                    icon={<SafetyOutlined/>}
                                    onClick={async () => {
                                        await fetchReleaseFiles(folderName, setUploadedFiles, setRefreshing, {
                                            validate: true,
                                            presign: true,
                                        });
                                        message.success('Files refreshed and validated');
                                    }}
                                    disabled={uploading || refreshing}
                                    loading={refreshing}
                                >
                                    Refresh Files
                                </Button>
                            </Tooltip>
                            <Button
                                type="primary"
                                icon={<UploadOutlined/>}
                                onClick={() => setUploadModalVisible(true)}
                                disabled={uploading || refreshing}
                            >
                                Upload More
                            </Button>
                        </Space>
                    }
                >
                    <Typography.Paragraph type="secondary" style={{marginBottom: 16}}>
                        Review your uploaded files below. These will be used in the next steps of the release process.
                        Files are automatically validated when loaded. Click the "Refresh Files" button to revalidate
                        files if needed.
                    </Typography.Paragraph>
                    <Table
                        dataSource={uploadedFiles}
                        columns={columns}
                        rowKey="uid"
                        rowSelection={rowSelection}
                        pagination={false}
                        loading={uploading || refreshing || bulkDeleting}
                    />
                </ProCard>
            )}

            <Modal
                open={previewVisible}
                title={previewTitle}
                footer={null}
                onCancel={() => setPreviewVisible(false)}
            >
                <Image
                    alt={previewTitle}
                    style={{width: '100%'}}
                    src={previewImage}
                />
            </Modal>

            {/* Upload Modal */}
            <Modal
                open={uploadModalVisible}
                title="Upload Files"
                footer={null}
                onCancel={() => setUploadModalVisible(false)}
                width={700}
            >
                {uploading ? (
                    <div style={{textAlign: 'center', padding: '50px 0'}}>
                        <Typography.Text>Upload in progress. Please wait...</Typography.Text>
                        <Progress percent={Math.round(uploadProgress)} status="active"
                                  style={{width: '100%', marginTop: 16}}/>
                    </div>
                ) : (
                    <ProFormUploadDragger
                        name="modal_files"
                        fieldProps={{
                            multiple: true,
                            showUploadList: false,
                            beforeUpload: (file, fileList) => {
                                // For the first file in the batch or individual file
                                if (fileList.indexOf(file) === 0) {
                                    handleUploadMultiple(fileList);
                                    // Close the modal after upload starts
                                    setUploadModalVisible(false);
                                }
                                return false; // Prevent default upload behavior
                            }
                        }}
                        title={null}
                        description={
                            <Space direction="vertical" size="small" style={{textAlign: 'center', width: '100%'}}>
                                <Typography.Text strong>Click or drag files to this area to upload</Typography.Text>
                                <Typography.Text type="secondary">
                                    Supports batch uploads for images (JPG, PNG, etc.) and audio (MP3, WAV, etc.).
                                </Typography.Text>
                                <Space>
                                    <Tag icon={<FileImageOutlined/>} color="blue">Images</Tag>
                                    <Tag icon={<CustomerServiceOutlined/>} color="green">Audio</Tag>
                                </Space>
                            </Space>
                        }
                        style={{
                            padding: '40px 20px',
                            background: '#fafafa',
                            border: '2px dashed #d9d9d9',
                            borderRadius: '8px'
                        }}
                    />
                )}
            </Modal>

            {/* Validation Details Modal */}
            <Modal
                open={validationModalVisible}
                title={selectedFile ? `Validation Results: ${selectedFile.name}` : 'Validation Results'}
                footer={[
                    <Button key="close" onClick={() => setValidationModalVisible(false)}>
                        Close
                    </Button>
                ]}
                onCancel={() => setValidationModalVisible(false)}
                width={700}
            >
                {selectedFile && selectedFile.validationResult && (
                    <div>
                        <div style={{marginBottom: 16}}>
                            <Typography.Title level={5}>Overall Status</Typography.Title>
                            {selectedFile.validationResult.isValid ? (
                                <Tag color="success" icon={<CheckCircleOutlined/>}>Valid</Tag>
                            ) : (
                                <Tag color="warning" icon={<WarningOutlined/>}>Invalid</Tag>
                            )}

                            {selectedFile.validationResult.issues && selectedFile.validationResult.issues.length > 0 && (
                                <div style={{marginTop: 8}}>
                                    <Typography.Text strong>Issues:</Typography.Text>
                                    <ul>
                                        {selectedFile.validationResult.issues.map((issue, index) => (
                                            <li key={index}><Typography.Text type="warning">{issue}</Typography.Text>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        <Typography.Title level={5}>Platform-Specific Validation</Typography.Title>
                        <Collapse>
                            {selectedFile.validationResult.platformResults && Object.entries(selectedFile.validationResult.platformResults).map(([platform, result]) => (
                                <Collapse.Panel
                                    key={platform}
                                    header={
                                        <Space>
                                            <span style={{textTransform: 'capitalize'}}>{platform}</span>
                                            {result.isValid ? (
                                                <Tag color="success" icon={<CheckCircleOutlined/>}>Valid</Tag>
                                            ) : (
                                                <Tag color="warning" icon={<WarningOutlined/>}>Invalid</Tag>
                                            )}
                                        </Space>
                                    }
                                >
                                    {result.issues && result.issues.length > 0 ? (
                                        <ul>
                                            {result.issues.map((issue, index) => (
                                                <li key={index}><Typography.Text type="warning">{issue}</Typography.Text>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <Typography.Text>No issues found.</Typography.Text>
                                    )}
                                </Collapse.Panel>
                            ))}
                        </Collapse>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default FileUploadStep;
